/**
 * Generate a map of path segments that should be expanded based on search paths
 * @param paths Array of full paths that matched a search
 * @returns Object with path segments as keys and true as values
 */
export const generateExpansionsFromPaths = (
    paths: string[],
): Record<string, boolean> => {
    const expansions: Record<string, boolean> = {};

    paths.forEach((path) => {
        // Split the path into segments
        const segments = path.split('.');

        // For each partial path (parent paths), mark as expanded
        let currentPath = '';
        for (let i = 0; i < segments.length; i++) {
            if (i === 0) {
                currentPath = segments[i];
            } else {
                currentPath += '.' + segments[i];
            }
            expansions[currentPath] = true;

            // Add array-specific formats to handle array indices properly
            if (!isNaN(Number(segments[i]))) {
                expansions[`array-item-${currentPath}`] = true;
                expansions[`list-item-${currentPath}`] = true;

                // Handle the parent container of this array item
                if (i > 0) {
                    const parentPath = segments.slice(0, i).join('.');
                    const arrayIndex = segments[i];
                    expansions[`exact-array-${parentPath}-${arrayIndex}`] =
                        true;
                }
            }
        }
    });

    return expansions;
};

/**
 * Utility to check if a path should be expanded based on a search
 * @param path The current path to check
 * @param searchPath The search path to match against
 * @returns Boolean indicating if the path should be expanded
 */
export const shouldExpandPath = (path: string, searchPath: string): boolean => {
    if (!searchPath) return false;

    const normalizedPath = path.toLowerCase();
    const normalizedSearch = searchPath.toLowerCase();

    // Direct match
    if (normalizedPath === normalizedSearch) return true;

    // Path is parent of search path
    if (normalizedSearch.startsWith(normalizedPath + '.')) return true;

    // Path is on the way to search path
    if (searchPath.includes('.')) {
        const searchSegments = normalizedSearch.split('.');
        const pathSegments = normalizedPath.split('.');

        // Check if path segments match the beginning of search segments
        if (pathSegments.length < searchSegments.length) {
            for (let i = 0; i < pathSegments.length; i++) {
                if (pathSegments[i] !== searchSegments[i]) {
                    return false;
                }
            }
            return true;
        }
    }

    return false;
};
